<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { toast } from '@/ui-plus/sonner';
import type { ServiceFee } from '@lib/zod/serviceFee';
import ServiceFeeDialog from '../ServiceFeeDialog.vue';
// import PlatformServiceFeeOverrideDialog from './PlatformServiceFeeOverrideDialog.vue';
import { Badge } from '@/components/ui/badge';
import { zmgAdminOnlyCacheUpdateClient, zmgPublicCacheClient } from '@api-client/zmg_cache';
// import { authState } from '@/ui-plus/useAuth';

type DeliveryteamsResponse = ResType<typeof dashboardApiClient.website.supported_deliveryteams.list[':websiteId']['$get']>["data"];

const props = defineProps<{
    deliveryteam: DeliveryteamsResponse['deliveryteams'][number];
}>();

const moreDeliveryteamDetails = ref<{
    overridePartnerFee?: ServiceFee;
}>({
    overridePartnerFee: undefined,
});

// const isPlatformFeeAdmin = authState.value.accessTokenPayload?.email === 'kerem-dev2@faircarts.com' || // DEV
//     authState.value.accessTokenPayload?.email === 'onlinebite45@gmail.com'; // PROD

onMounted(async () => {
    const { data, error } = await (await zmgPublicCacheClient.website.fee_override_as_deliveryteam_support_team[':id'][':cId']['data.json'].$get({
        param: { id: currentWebsiteId.value ?? '', cId: props.deliveryteam.id },
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to load deliveryteam allowlist');
    }
    if (data) {
        console.log('data', data);
        moreDeliveryteamDetails.value.overridePartnerFee = data.overridePartnerFee ?? undefined
    }
});

const upsertDeliveryteam = async (deliveryteamId: string, overridePartnerFee?: ServiceFee) => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.fee_override_as_deliveryteam_support_team[':id'][':cId'].$patch({
        param: { id: currentWebsiteId.value ?? '', cId: deliveryteamId },
        json: { overridePartnerFee: overridePartnerFee ?? null }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to save deliveryteam');
    } else {
        toast.success('saved');
        moreDeliveryteamDetails.value.overridePartnerFee = overridePartnerFee ?? undefined;
    }
};

</script>

<template>
    <div class="flex flex-col">
        <span class="font-medium">{{ deliveryteam.name }}</span>
        <span v-if="deliveryteam.address1"
              class="text-sm text-muted-foreground">
            {{ deliveryteam.address1 }}
        </span>
        <span v-if="deliveryteam.city"
              class="text-sm text-muted-foreground">
            {{ deliveryteam.city }}
        </span>
        <span v-if="deliveryteam.country"
              class="text-sm text-muted-foreground">
            {{ deliveryteam.country }}
        </span>
        <span class="text-sm text-muted-foreground">
            ({{ deliveryteam.isActive === true ? 'Active' : 'Inactive' }})
        </span>
        <span v-if="deliveryteam.isSuspended === true"
              class="text-xs text-destructive">
            <OctagonX class="h-4 w-4 mr-1" />
            Suspended
        </span>
        <span class="text-xs text-muted-foreground">{{ deliveryteam.id }}</span>
    </div>
    <div class="flex items-center gap-2">
        <div v-if="moreDeliveryteamDetails.overridePartnerFee">
            <Badge variant="outline">
                {{ moreDeliveryteamDetails.overridePartnerFee?.ratePerOrder }}%
            </Badge>
            <Badge variant="outline">
                +{{ moreDeliveryteamDetails.overridePartnerFee?.feePerOrder }}
            </Badge>
        </div>
        <ServiceFeeDialog :id="deliveryteam.id"
                          :override-fee="moreDeliveryteamDetails.overridePartnerFee ?? {}"
                          @upsert="(id, overridePartnerFee) => {
                            console.log('overridePartnerFee', overridePartnerFee);
                            moreDeliveryteamDetails.overridePartnerFee = overridePartnerFee ?? undefined;
                            upsertDeliveryteam(id, overridePartnerFee ?? undefined);
                        }" />
        <!-- <PlatformServiceFeeOverrideDialog v-if="isPlatformFeeAdmin"
                                          seller-type="deliveryteam"
                                          :seller-id="deliveryteam.id" /> -->
    </div>
</template>
